/**
 * userContext — helpers to persist/read user diagnostic context across pages.
 *
 * Used to chain personalization between /resultats and /prsnl:
 * when a user submits a quiz, we store key data (resto_name, first_name, ca, food cost...)
 * in localStorage. Then on /prsnl, we can personalize the page if data is found.
 */

const STORAGE_KEY = 'prsnl_user_context';
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export const saveUserContext = (ctx) => {
  if (typeof window === 'undefined') return;
  try {
    const payload = {
      data: ctx,
      savedAt: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (_) {
    // ignore (quota, private mode, etc.)
  }
};

export const readUserContext = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.data || !parsed.savedAt) return null;
    if (Date.now() - parsed.savedAt > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed.data;
  } catch (_) {
    return null;
  }
};

export const clearUserContext = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (_) {}
};

/**
 * Map user context → suggested PRSNL offer tab.
 * Used to auto-select the right pricing tab when user lands on /prsnl from /resultats.
 *
 * Returns one of: 'audit_day' | 'refonte' | 'ouverture' | 'groupe' | null
 */
export const getSuggestedOffer = (ctx) => {
  if (!ctx) return null;
  // Priority 1 : explicit project status from quiz
  if (ctx.projectStatus === 'en_ouverture') return 'ouverture';
  if (ctx.projectStatus === 'multi_resto') return 'groupe';
  if (ctx.projectStatus === 'reflexion') return 'audit_day';
  // Priority 2 : CA-based fallback for already-open restaurants
  if (ctx.projectStatus === 'deja_ouvert' || !ctx.projectStatus) {
    if (ctx.ca && ctx.ca < 15000) return 'audit_day';
    return 'refonte';
  }
  return 'refonte';
};

/**
 * Pretty label for the suggested offer (for personalization in copy).
 */
export const getSuggestedOfferLabel = (offer) => {
  switch (offer) {
    case 'audit_day': return '1 Day · Audit terrain';
    case 'refonte': return 'Refonte 90 jours';
    case 'ouverture': return 'Ouverture 3 mois';
    case 'groupe': return 'Groupe multi-resto';
    default: return null;
  }
};
